import { useState, useRef, useEffect } from 'react';
import { useNotify } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField as MuiTextField,
  Alert,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { EmailHtmlEditor } from './EmailHtmlEditor';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { useClientSearch, ClientSearchResult } from '../hooks/useClientSearch';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

// "Caroline Storr" -> { first: 'Caroline', last: 'Storr' }. Only used to seed
// the form fields for display and the email greeting; the backend prefers the
// person's stored first/last name when one was picked.
const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
};

/**
 * Manually create a lead from the Leads page. Mirrors the QualifyDialog
 * (same fields, same editable email preview) but adds a Phone field and a
 * "Qualify this lead" toggle. When the toggle is off the lead is created at
 * stage 'new' and the email section is hidden. POSTs to /leads/manual.
 *
 * The person can either be typed in fresh or picked from the existing people.
 * Picking matters for repeat families: without it the backend resolves someone
 * by email or phone, and a near-miss (or a shared family address) either mints
 * a duplicate person or attaches the lead to the wrong one.
 */
export function CreateLeadDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  // The picked person, or null while creating a new one.
  const [client, setClient] = useState<ClientSearchResult | null>(null);
  const [clientQuery, setClientQuery] = useState('');
  const { results: clientResults, loading: clientsLoading } = useClientSearch(clientQuery);
  // Only meaningful for a picked person who already has an open lead.
  const [newEpisode, setNewEpisode] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('en');
  const [province, setProvince] = useState<'quebec' | 'ontario'>('quebec');
  const [dealStage, setDealStage] = useState<'at_need' | 'pre_need'>('at_need');
  const [cremationCategory, setCremationCategory] = useState('');
  const [relationship, setRelationship] = useState('');
  const [qualify, setQualify] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  // Editable email preview state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewKeyRef = useRef<string | null>(null);

  // Reset the form each time the dialog opens.
  const handleOpen = () => {
    setClient(null);
    setClientQuery('');
    setNewEpisode(true);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setLanguage('en');
    setProvince('quebec');
    setDealStage('at_need');
    setCremationCategory('');
    setRelationship('');
    setQualify(true);
    setSendEmail(true);
    setEmailSubject('');
    setEmailHtml('');
    setPreviewError(null);
    previewKeyRef.current = null;
  };

  // Picking someone fills the identity fields from their record and locks them;
  // clearing the pick hands the form back for a new person.
  const handleClientChange = (picked: ClientSearchResult | null) => {
    setClient(picked);
    setNewEpisode(true);
    if (picked) {
      const { first, last } = splitName(picked.name || '');
      setFirstName(first);
      setLastName(last);
      setEmail(picked.email || '');
      setPhone(picked.phone_number || '');
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
    }
  };

  // Load (or reload) the editable preview when qualifying + sending email.
  useEffect(() => {
    if (!open || !qualify || !sendEmail || !email.trim()) return;
    const key = `${dealStage}|${language}|${province}|${firstName}`;
    if (previewKeyRef.current === key) return;

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${baseUrl}/leads/quote-email-preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Refresh-Token': await getRefreshToken(),
          },
          body: JSON.stringify({ dealStage, language, province, firstName: firstName.trim() || undefined }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `Preview failed (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;
        // Mark this key loaded only on success — otherwise a fetch cancelled by
        // StrictMode's double-invoke (or a fast dep change) would dedupe out its
        // own replacement and leave previewLoading stuck on "Loading…".
        previewKeyRef.current = key;
        setEmailSubject(data.subject || '');
        setEmailHtml(data.html || '');
      } catch (e: any) {
        if (!cancelled) setPreviewError(e.message || 'Failed to load email preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, qualify, sendEmail, dealStage, language, province, firstName, email]);

  const handleSubmit = async () => {
    if (!client && !email.trim() && !phone.trim()) {
      notify('Email or phone is required', { type: 'warning' });
      return;
    }
    if (qualify && dealStage === 'at_need' && !cremationCategory) {
      notify('Timeline is required for at-need leads', { type: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/leads/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({
          dealStage,
          ...(client
            ? { clientId: client.id, newEpisode: client.has_open_lead && newEpisode }
            : {}),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          language,
          province,
          cremationCategory: dealStage === 'at_need' ? cremationCategory : undefined,
          relationship: dealStage === 'at_need' ? relationship : undefined,
          qualify,
          sendEmail: qualify && sendEmail,
          // Only send overrides when the preview successfully loaded — otherwise
          // fall back to the SendGrid template path on the backend.
          ...(qualify && sendEmail && emailSubject && emailHtml
            ? { customSubject: emailSubject, customHtml: emailHtml }
            : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed (${response.status})`);
      }

      notify(qualify ? 'Lead created and qualified' : 'Lead created', { type: 'success' });
      onClose();
      onCreated?.();
    } catch (e: any) {
      notify(e.message || 'Failed to create lead', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionProps={{ onEnter: handleOpen }}>
      <DialogTitle>New Lead</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Autocomplete
          options={clientResults}
          value={client}
          onChange={(_, picked) => handleClientChange(picked)}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setClientQuery(value);
          }}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={clientsLoading}
          // The search is server-side; MUI must not re-filter what came back.
          filterOptions={(x) => x}
          noOptionsText={
            clientQuery.trim().length < 2 ? 'Type to search people…' : 'No match — leave blank to create a new person'
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {[option.email, option.phone_number, option.city].filter(Boolean).join(' · ') || 'No contact details'}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Existing person (optional)"
              size="small"
              placeholder="Search by name, email or phone"
              helperText="Leave blank to create a new person from the fields below."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {clientsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {client && client.has_open_lead && (
          <Alert severity="info" sx={{ py: 0 }}>
            {client.name} already has an open lead.
            <FormControlLabel
              sx={{ display: 'block', mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={newEpisode}
                  onChange={(e) => setNewEpisode(e.target.checked)}
                />
              }
              label={
                newEpisode
                  ? 'Open a separate lead (use this for a second death in the family)'
                  : 'Add to their existing lead instead'
              }
            />
          </Alert>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <MuiTextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size="small"
            fullWidth
            disabled={!!client}
          />
          <MuiTextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="small"
            fullWidth
            disabled={!!client}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <MuiTextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            fullWidth
            disabled={!!client}
          />
          <MuiTextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
            fullWidth
            disabled={!!client}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Language</InputLabel>
            <Select value={language} label="Language" onChange={(e) => setLanguage(e.target.value)}>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="fr">French</MenuItem>
            </Select>
          </FormControl>
          <FormControl component="fieldset">
            <RadioGroup row value={province} onChange={(e) => setProvince(e.target.value as any)}>
              <FormControlLabel value="quebec" control={<Radio size="small" />} label="Quebec" />
              <FormControlLabel value="ontario" control={<Radio size="small" />} label="Ontario" />
            </RadioGroup>
          </FormControl>
        </div>

        <FormControl component="fieldset">
          <FormLabel component="legend">Deal Stage *</FormLabel>
          <RadioGroup row value={dealStage} onChange={(e) => { setDealStage(e.target.value as any); setCremationCategory(''); }}>
            <FormControlLabel value="at_need" control={<Radio />} label="At-Need" />
            <FormControlLabel value="pre_need" control={<Radio />} label="Pre-Need" />
          </RadioGroup>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>{qualify && dealStage === 'at_need' ? 'Timeline *' : 'Timeline'}</InputLabel>
          <Select value={cremationCategory} label={qualify && dealStage === 'at_need' ? 'Timeline *' : 'Timeline'} onChange={(e) => setCremationCategory(e.target.value)}>
            {dealStage === 'at_need' ? [
              <MenuItem key="passed" value="Passed">Passed</MenuItem>,
              <MenuItem key="imm17" value="Imminent (1-7 days)">Imminent (1-7 days)</MenuItem>,
              <MenuItem key="near" value="Near future (7-30 days)">Near future (7-30 days)</MenuItem>,
              <MenuItem key="pre" value="Preplanning (30+ days)">Preplanning (30+ days)</MenuItem>,
            ] : [
              <MenuItem key="planning" value="Planning now">Planning now</MenuItem>,
              <MenuItem key="followup" value="Follow up">Follow up</MenuItem>,
            ]}
          </Select>
        </FormControl>

        {dealStage === 'at_need' && (
          <FormControl size="small" fullWidth>
            <InputLabel>Relationship</InputLabel>
            <Select value={relationship} label="Relationship" onChange={(e) => setRelationship(e.target.value)}>
              <MenuItem value="spouse">Spouse/Partner</MenuItem>
              <MenuItem value="child">Child</MenuItem>
              <MenuItem value="parent">Parent</MenuItem>
              <MenuItem value="sibling">Sibling</MenuItem>
              <MenuItem value="friend">Friend</MenuItem>
              <MenuItem value="other-family">Other Family Member</MenuItem>
              <MenuItem value="legal-rep">Legal Representative</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        )}

        <Divider />

        <FormControlLabel
          control={<Checkbox checked={qualify} onChange={(e) => setQualify(e.target.checked)} />}
          label="Qualify this lead"
        />

        {qualify && (
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={!email.trim()}
              />
            }
            label="Send quote email"
          />
        )}

        {qualify && sendEmail && email.trim() && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>
              Preview &amp; edit the email before it goes out. The quote link is inserted automatically after qualification.
            </div>
            {previewLoading && (
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>Loading template…</div>
            )}
            {previewError && (
              <Alert severity="warning">
                {previewError} — the default template will be sent as-is.
              </Alert>
            )}
            {!previewLoading && !previewError && (emailSubject || emailHtml) && (
              <>
                <MuiTextField
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  size="small"
                  fullWidth
                />
                <EmailHtmlEditor html={emailHtml} onChange={setEmailHtml} />
              </>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Creating...' : qualify ? 'Create & Qualify' : 'Create Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
